import React, { useEffect, useRef } from 'react';
import { Card, CardContent, Typography, CircularProgress } from '@mui/material';
import { Chart } from 'chart.js/auto';
import { useHistoricalData } from '../hooks/useHistoricalData';
import { useLatestData } from '../hooks/useLatestData';
import { toZonedTime, format } from 'date-fns-tz';
import { kphToMph, getTimeRange } from '../utils/utils'; // Added getTimeRange
import 'chartjs-adapter-date-fns';
import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);

const highlightPlugin = {
  id: 'highlightPlugin',
  afterTooltipDraw: (chart) => {
    const tooltip = chart.tooltip;
    const datasets = chart.data.datasets;

    datasets.forEach((dataset) => {
      if (
        dataset._originalPointRadius &&
        dataset._originalPointBackgroundColor &&
        dataset._originalPointBorderWidth
      ) {
        dataset.pointRadius = [...dataset._originalPointRadius];
        dataset.pointBackgroundColor = [...dataset._originalPointBackgroundColor];
        dataset.pointBorderWidth = [...dataset._originalPointBorderWidth];
      }
    });

    if (tooltip && tooltip.getActiveElements().length > 0) {
      tooltip.getActiveElements().forEach((activeElement) => {
        const dataset = datasets[activeElement.datasetIndex];
        const index = activeElement.index;

        if (!dataset._originalPointRadius) {
          dataset._originalPointRadius = [...dataset.pointRadius];
          dataset._originalPointBackgroundColor = [...dataset.pointBackgroundColor];
          dataset._originalPointBorderWidth = [...dataset.pointBorderWidth];
        }

        dataset.pointRadius[index] = 4;
        dataset.pointBackgroundColor[index] = '#000000';
        dataset.pointBorderWidth[index] = 0;
      });
    }
  },
};

const WindSpeedHistory = ({
  site,
  dateRange,
  timezone,
  refreshInterval,
  latestData: latestDataFromProps,
  latestDataLoading: latestDataLoadingFromProps,
  latestDataError: latestDataErrorFromProps,
}) => {
  const { combinedData: historicalData, loading: historicalLoading, error: historicalError } = useHistoricalData(site, dateRange, refreshInterval);
  const hasSharedLatestData = latestDataFromProps !== undefined;
  const { latestData: hookLatestData, loading: hookLatestLoading, error: hookLatestError } = useLatestData(site, refreshInterval, !hasSharedLatestData);
  const latestData = hasSharedLatestData ? latestDataFromProps : hookLatestData;
  const latestLoading = hasSharedLatestData ? Boolean(latestDataLoadingFromProps) : hookLatestLoading;
  const latestError = hasSharedLatestData ? latestDataErrorFromProps : hookLatestError;
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const lastX = useRef(null);

  useEffect(() => {
    if (!chartRef.current || historicalLoading || historicalError || latestLoading || latestError || historicalData.length === 0) return;

    // Define the time range for the current dateRange
    const range = getTimeRange(dateRange);
    const rangeStart = range.start;
    const rangeEnd = range.end;

    // Combine historical data with latest data, only if within range
    const combinedData = [...historicalData];
    if (latestData && latestData.time_utc) {
      const latestTime = new Date(latestData.time_utc).getTime();
      if (latestTime >= rangeStart && latestTime <= rangeEnd) {
        const latestExists = combinedData.some((d) => new Date(d.time_utc).getTime() === latestTime);
        if (!latestExists) {
          combinedData.push({
            time_utc: latestData.time_utc,
            wind_speed_mph: latestData.wind_speed_kph != null ? kphToMph(latestData.wind_speed_kph) : null,
            wind_gust_mph: latestData.wind_gust_kph != null ? kphToMph(latestData.wind_gust_kph) : null,
          });
        }
      }
    }
    combinedData.sort((a, b) => new Date(a.time_utc).getTime() - new Date(b.time_utc).getTime());

    const labels = combinedData
      .map((data) => {
        const date = new Date(data.time_utc);
        return isNaN(date.getTime()) ? null : toZonedTime(date, timezone);
      })
      .filter((date) => date !== null);

    const windSpeeds = combinedData.map((data) => data.wind_speed_mph);
    const windGusts = combinedData.map((data) => data.wind_gust_mph);

    const minTime = Math.min(...labels.map((d) => d.getTime()));
    const maxTime = Math.max(...labels.map((d) => d.getTime()));

    const cursorLinePlugin = {
      id: 'cursorLine',
      afterEvent: (chart, args) => {
        const { event } = args;
        if (event.type === 'mousemove') {
          const x = chart.scales.x.getValueForPixel(event.x);
          if (x !== lastX.current) {
            lastX.current = x;
            chart.options.plugins.annotation.annotations.cursorLine.xMin = x;
            chart.options.plugins.annotation.annotations.cursorLine.xMax = x;
            chart.update('none');
          }
        }
      },
    };

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: 'Wind Speed (mph)',
          data: windSpeeds,
          fill: false,
          borderColor: 'rgba(22, 122, 104, 0.9)',
          borderWidth: 2,
          tension: 0.1,
          pointRadius: Array(windSpeeds.length).fill(dateRange === 'last12h' || dateRange === 'last4h' ? 3 : 0),
          pointBackgroundColor: Array(windSpeeds.length).fill('rgba(22, 122, 104, 0.9)'),
          pointBorderWidth: Array(windSpeeds.length).fill(1),
          pointHitRadius: dateRange === 'last12h' || dateRange === 'last4h' ? 10 : 5,
          hidden: false,
        },
        {
          label: 'Wind Gust (mph)',
          data: windGusts,
          fill: false,
          borderColor: 'rgba(227, 83, 2, 0.88)',
          borderWidth: 2,
          tension: 0.1,
          pointRadius: Array(windGusts.length).fill(dateRange === 'last12h' || dateRange === 'last4h' ? 3 : 0),
          pointBackgroundColor: Array(windGusts.length).fill('rgba(227, 83, 2, 0.88)'),
          pointBorderWidth: Array(windGusts.length).fill(1),
          pointHitRadius: dateRange === 'last12h' || dateRange === 'last4h' ? 10 : 5,
          hidden: false,
        },
      ],
    };

    const chartOptions = {
      animation: { duration: 200 },
      plugins: {
        datalabels: { display: false },
        legend: {
          onClick: (e, legendItem, legend) => {
            const chart = legend.chart;
            const index = legendItem.datasetIndex;
            const ci = chart.data.datasets[index];
            if (!ci) return;

            const currentVisibility = ci.hidden || false;
            ci.hidden = !currentVisibility;
            chart.update();
          },
          labels: {
            generateLabels: (chart) => {
              return chart.data.datasets.map((dataset, i) => ({
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: 0,
                hidden: dataset.hidden || false,
                index: i,
              }));
            },
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          caretPadding: 15,
          caretSize: 0,
          position: 'nearest',
          animation: { duration: 200 },
          callbacks: {
            title: (tooltipItems) => {
              if (!tooltipItems || tooltipItems.length === 0) return '';
              const utcTime = new Date(tooltipItems[0].parsed.x);
              return format(utcTime, 'MM/dd HH:mm', { timeZone: 'UTC' });
            },
            label: (context) => {
              const datasetLabel = context.dataset.label;
              const value = context.parsed.y;
              return datasetLabel === 'Wind Gust (mph)'
                ? `Wind Gust: ${value != null ? value.toFixed(1) : 'N/A'} mph`
                : `Wind Speed: ${value != null ? value.toFixed(1) : 'N/A'} mph`;
            },
            labelColor: (context) => ({
              backgroundColor: context.dataset.borderColor,
              borderColor: context.dataset.borderColor,
            }),
          },
          filter: (tooltipItem) => {
            const x = chartInstance.current?.options.plugins.annotation.annotations.cursorLine.xMin;
            if (!x) return false;
            const labelTime = labels[tooltipItem.dataIndex].getTime();
            const threshold = 3600000 / 2;
            return Math.abs(labelTime - x) <= threshold;
          },
          external: (context) => {
            const tooltipModel = context.tooltip;
            if (!tooltipModel || !tooltipModel.chart) return;
            const chart = tooltipModel.chart;
            const chartArea = chart.chartArea;
            const cursorX = chart.scales.x.getPixelForValue(lastX.current);

            const leftQuarterThreshold = chartArea.left + chartArea.width * 0.25;
            tooltipModel.xAlign = cursorX < leftQuarterThreshold ? 'right' : 'left';
          },
        },
        annotation: {
          drawTime: 'afterDatasetsDraw',
          annotations: {
            cursorLine: {
              type: 'line',
              xMin: null,
              xMax: null,
              borderColor: 'rgba(0, 0, 0, 0.5)',
              borderWidth: 1,
              borderDash: [5, 5],
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'hour',
            stepSize: 1,
            displayFormats: { hour: 'MM/dd HH:mm' },
          },
          min: minTime,
          max: maxTime,
          title: { display: true, text: 'Time' },
        },
        y: {
          title: { display: true, text: 'Wind Speed (mph)' },
          beginAtZero: true,
        },
      },
    };

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    chartInstance.current = new Chart(chartRef.current, {
      type: 'line',
      data: chartData,
      options: chartOptions,
      plugins: [cursorLinePlugin, highlightPlugin],
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [historicalData, historicalLoading, historicalError, latestData, latestLoading, latestError, timezone, dateRange]);

  if (historicalLoading || latestLoading) {
    return (
      <Card sx={{ height: 420 }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <CircularProgress sx={{ color: 'var(--color-brand)' }} />
        </CardContent>
      </Card>
    );
  }

  if (historicalError || latestError) {
    return (
      <Card sx={{ height: 420 }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="error">Error: {historicalError || latestError}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!historicalData || historicalData.length === 0) {
    return (
      <Card sx={{ height: 420 }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography>No wind speed data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: 420, overflow: 'hidden' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.2 }}>
        <Typography variant="h6" sx={{ color: 'var(--color-brand-dark)', mb: 0.25 }}>
          Wind Speed History
        </Typography>
        <Typography variant="caption" sx={{ color: 'var(--color-muted)', mb: 1 }}>
          Speed and gust trends across selected time range
        </Typography>
        <canvas ref={chartRef} style={{ flex: 1, maxHeight: '348px' }} />
      </CardContent>
    </Card>
  );
};

export default WindSpeedHistory;
